import { FormProvider, useForm } from "react-hook-form";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { ActivityCategoryPicker } from "@/forms/AddActivityForm/ActivityCategoryPicker";
import {
  categorySchema,
  orderedCategories,
  type IActivityCategory,
} from "@medoma/models/activities";
import type { IPatientId } from "@medoma/models/patients";
import { useIsGroupedCategory } from "@/api/ActivityGrouping";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { newActivityRow, type ActivityRowFields } from "./activityRow";
import { GroupedRetroForm } from "./GroupedRetroForm";
import { PostRegisterTemplates } from "./PostRegisterTemplates";
import { SchedulingFields, type ISchedulingFields } from "./SchedulingFields";
import { SingleRetroForm } from "./SingleRetroForm";
import styles from "./RegisterActivityRetroactivelyForm.module.scss";

export type IRegisterActivitiesRetroactivelyFormData = ISchedulingFields & {
  category: IActivityCategory;
  activities: ActivityRowFields[];
};

const DISALLOWED_CATEGORIES_FOR_RETROACTIVE_REGISTRATION: IActivityCategory[] =
  [categorySchema.enum.PatientTask, categorySchema.enum.PatientMeasurementTask];

export const RegisterActivityRetroactivelyForm = ({
  patientId,
}: {
  patientId: IPatientId;
}) => {
  const { data: phoneCallActivityTypeEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.PhoneCallActivityType,
  );
  const isGroupedCategory = useIsGroupedCategory();

  const allowedCategoriesForRetroactiveRegistration = orderedCategories.filter(
    (c) => {
      if (DISALLOWED_CATEGORIES_FOR_RETROACTIVE_REGISTRATION.includes(c)) {
        return false;
      }
      if (
        c === categorySchema.enum.PhoneCall &&
        !phoneCallActivityTypeEnabled
      ) {
        return false;
      }
      return true;
    },
  );

  const methods = useForm<IRegisterActivitiesRetroactivelyFormData>({
    defaultValues: {
      activities: [newActivityRow()],
    },
  });
  const {
    setValue,
    clearErrors,
    watch,
    formState: { errors, isDirty },
  } = methods;

  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const category = watch("category");

  return (
    <FormProvider {...methods}>
      <div className={styles.fields}>
        {errors.root?.server?.message && !isDirty ? (
          <ErrorMessage message={errors.root.server.message} />
        ) : null}
        <PostRegisterTemplates
          patientId={patientId}
          onApply={(applied) => {
            const options = { shouldDirty: true, shouldValidate: true };
            setValue("category", applied.category, options);
            setValue(
              "activities",
              [
                {
                  ...newActivityRow(),
                  title: applied.title,
                  durationOption: applied.durationOption,
                  procedureCodes: applied.procedureCodes,
                  productCodes: applied.productCodes,
                },
              ],
              options,
            );
            clearErrors("root.activities");
          }}
        />
        <ActivityCategoryPicker
          categories={allowedCategoriesForRetroactiveRegistration}
          errorMessage={errors.category?.message}
          onChange={() => {
            clearErrors("root.activities");
            setValue("activities", [newActivityRow()]);
          }}
        />
        <SchedulingFields />
        {category && isGroupedCategory(category) === true ? (
          <GroupedRetroForm
            key={category}
            patientId={patientId}
            category={category}
          />
        ) : category && isGroupedCategory(category) === false ? (
          <SingleRetroForm
            key={category}
            patientId={patientId}
            category={category}
          />
        ) : null}
      </div>
    </FormProvider>
  );
};
